import { Heading, Text } from "@afa-shared/afa-components";
import { ContentLink } from "@components/Link/ContentLink";
import { StandardLink } from "@components/Link/StandardLink";
import { getInternalOrExternalIcon } from "@utils/linkIconHelper";
import clsx from "clsx";

import { Context } from "types/context/Context";

import linkTeaserBlockStyles from "./linkTeaserBlock.module.css";
import {
  LinkTeaserBlockType_linkTeaserBlock,
  LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Faq,
  LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Link,
  LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Page,
} from "./queries/contentfulTypes/LinkTeaserBlockType";

interface ILinkTeaserBlockProps {
  blockData: LinkTeaserBlockType_linkTeaserBlock;
  context?: Context;
}

type Faq_item_intercepted = LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Faq & {
  url?: string | null;
};
type Page_item_intercepted = LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Page & {
  url?: string | null;
};

type LinksCollection_items_intercepted =
  | LinkTeaserBlockType_linkTeaserBlock_linksCollection_items_Link
  | Faq_item_intercepted
  | Page_item_intercepted;

export const Links = ({
  links,
  context,
}: {
  links?: LinksCollection_items_intercepted[];
  context: Context;
}) => {
  const renderedLinks = links?.map((link, index) => {
    if (!link?.url) return null;

    switch (link.__typename) {
      case "Faq":
        return (
          <ContentLink
            href={link?.url}
            className={linkTeaserBlockStyles.faq}
            prefetch={false}
            key={index}
            trackingData={{
              sectionTitle:
                context?.accordionHeading ??
                context?.sectionBlock?.sectionBlockHeading ??
                context?.pageHeading,
              sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex,
              insurance: context?.insuranceName || undefined,
            }}
            contentText={link?.question}
          >
            {/* TODO: Replace with <Icon> from @afa-shared/afa-components when question-mark icon is available */}
            <div className={linkTeaserBlockStyles.faqIcon} aria-hidden>
              <span>?</span>
            </div>
            <Text variant="link">{link?.question}</Text>
          </ContentLink>
        );
      case "Page":
        return (
          <StandardLink
            key={index}
            text={link?.teaserLinkText}
            href={link?.url}
            trackingData={{
              sectionTitle:
                context?.accordionHeading ??
                context?.sectionBlock?.sectionBlockHeading ??
                context?.pageHeading,
              sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex,
              insurance: context?.insuranceName || undefined,
            }}
          />
        );
      case "Link":
        const linkType = getInternalOrExternalIcon(
          link?.url,
          link?.reference,
          link?.globalLinkItem,
        );
        return (
          <StandardLink
            key={index}
            text={link?.linkText}
            href={link?.url}
            type={linkType}
            trackingData={{
              sectionTitle:
                context?.accordionHeading ??
                context?.sectionBlock?.sectionBlockHeading ??
                context?.pageHeading,
              sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex,
              insurance: context?.insuranceName || undefined,
            }}
          />
        );
      default:
        return null;
    }
  });

  return <div className={linkTeaserBlockStyles.linksWrapper}>{renderedLinks}</div>;
};

export const LinkTeaserBlock = ({ blockData: linkTeaserBlock, context }: ILinkTeaserBlockProps) => {
  return (
    <div
      data-contentful-entry-id={linkTeaserBlock?.sys?.id}
      data-contentful-field-id="name"
      className={clsx(linkTeaserBlockStyles.wrapper, {
        [linkTeaserBlockStyles.noBorder]: context?.sectionBlock?.coloredSection,
      })}
    >
      {linkTeaserBlock?.heading && (
        <Heading
          variant={context?.heading?.parentLevel}
          children={linkTeaserBlock?.heading}
          removeMargin
          className={linkTeaserBlockStyles.heading}
        />
      )}
      {linkTeaserBlock?.text && (
        <Text variant="paragraph" children={linkTeaserBlock?.text} removeMargin />
      )}
      {linkTeaserBlock?.linksCollection?.items && (
        <Links links={linkTeaserBlock.linksCollection?.items} context={context} />
      )}
    </div>
  );
};
